/**
 * Opening Hours Management Web Component
 * Handles dynamic sections, time lines, and API communication for B2B customers
 */

class OpeningHoursManager extends HTMLElement {
  constructor() {
    super()
    
    // Constants
    this.API_BASE_URL = '/apps/indeks-store-locator/op-location'
    
    // LocationIQ configuration
    this.LOCATIONIQ_API_KEY = (window.theme?.sections?.opening_hours?.locationiqApiKey && window.theme.sections.opening_hours.locationiqApiKey.trim()) 
      ? window.theme.sections.opening_hours.locationiqApiKey 
      : ''
    this.LOCATIONIQ_STYLE_URL = 'https://tiles.locationiq.com/v3/streets/vector.json'
    
    // State
    this.loadingIndicator = null
    this.currentData = []
    this.mapsLoaded = false
    
    // Translation strings - loaded from Shopify theme translations
    this.t = window.theme?.strings?.opening_hours
    
    if (this.t) {
      this.init()
    } else {
      console.error('OpeningHoursManager: Missing translations')
    }
  }

  init() {
    this.createLoadingIndicator()
    this.bindEvents()
    this.loadExistingData()
  }

  createLoadingIndicator() {
    this.loadingIndicator = document.createElement('div')
    this.loadingIndicator.className = 'loading-indicator'
    this.loadingIndicator.innerHTML = `
      <div class="loading-spinner"></div>
      <p>${this.t.loading}</p>
    `
    
    // Add backdrop
    const backdrop = document.createElement('div')
    backdrop.className = 'loading-backdrop'
    this.backdrop = backdrop
    
    this.appendChild(backdrop)
    this.appendChild(this.loadingIndicator)
  }


  bindEvents() {
    // Add section button
    const addSectionBtn = this.querySelector('[data-js="add-section"]')
    if (addSectionBtn) {
      addSectionBtn.addEventListener('click', () => this.addSection())
    }

    // Save button
    const saveBtn = this.querySelector('[data-js="save"]')
    if (saveBtn) {
      saveBtn.addEventListener('click', () => this.saveOpeningHours())
    }

    // Delegate events for dynamic content
    this.addEventListener('click', this.handleDynamicClicks.bind(this))
    this.addEventListener('input', this.handleInputChanges.bind(this))
    this.addEventListener('blur', this.handleInputBlur.bind(this), true)
  }

  handleDynamicClicks(event) {
    const target = event.target

    if (target.dataset.js === 'add-line') {
      event.preventDefault()
      const section = target.closest('[data-js="section"]')
      this.addTimeLine(section)
    }

    if (target.dataset.js === 'remove-line') {
      event.preventDefault()
      this.removeTimeLine(target.closest('[data-js="time-line"]'))
    }

    if (target.dataset.js === 'remove-section') {
      event.preventDefault()
      this.removeSection(target.closest('[data-js="section"]'))
    }
  }

  handleInputChanges(event) {
    const target = event.target
    
    // Clean time inputs in real-time as user types
    if (target.dataset.js && target.dataset.js.includes('time-input')) {
      this.cleanTimeInput(target)
    }
    
    // Handle closed checkbox toggle
    if (target.dataset.js === 'closed-checkbox') {
      this.toggleClosedState(target)
    }
  }

  handleInputBlur(event) {
    const target = event.target
    
    // Clean and validate time inputs when user finishes typing
    if (target.dataset.js && target.dataset.js.includes('time-input')) {
      this.cleanTimeInput(target)
      if (target.value.trim()) {
        this.validateTimeInput(target, { report: true })
      } else {
        target.classList.remove('invalid')
        target.setCustomValidity('')
      }
    }
  }

  toggleClosedState(checkbox) {
    const timeLine = checkbox.closest('[data-js="time-line"]')
    const fromInput = timeLine.querySelector('[data-js*="from-input"]')
    const toInput = timeLine.querySelector('[data-js*="to-input"]')
    
    if (checkbox.checked) {
      // Line is closed - disable time inputs and clear values
      fromInput.disabled = true
      toInput.disabled = true
      fromInput.removeAttribute('required')
      toInput.removeAttribute('required')
      fromInput.value = ''
      toInput.value = ''
    } else {
      // Line is open - enable time inputs
      fromInput.disabled = false
      toInput.disabled = false
      fromInput.setAttribute('required', '')
      toInput.setAttribute('required', '')
    }
  }

  cleanTimeInput(input) {
    const originalValue = input.value
    const cursorPosition = input.selectionStart
    
    let cleanedValue = originalValue.replace(/[^0-9:]/g, '')
    
    const colonCount = (cleanedValue.match(/:/g) || []).length
    if (colonCount > 1) {
      const firstColonIndex = cleanedValue.indexOf(':')
      cleanedValue = cleanedValue.substring(0, firstColonIndex + 1) + 
                    cleanedValue.substring(firstColonIndex + 1).replace(/:/g, '')
    }
    
    if (cleanedValue.length > 5) {
      cleanedValue = cleanedValue.substring(0, 5)
    }
    
    if (cleanedValue !== originalValue) {
      input.value = cleanedValue
      
      const newCursorPosition = Math.min(cursorPosition, cleanedValue.length)
      input.setSelectionRange(newCursorPosition, newCursorPosition)
    }
  }

  parseTimeInput(value) {
    const parts = value.split(':')
    const hours = parseInt(parts[0], 10)
    const minutes = parts.length > 1 ? parseInt(parts[1], 10) : 0
    if (isNaN(hours) || isNaN(minutes)) return null
    return { hours, minutes }
  }

  timeToNumber(hours, minutes) {
    return hours + (minutes / 100)
  }

  validateTimeInput(input, { report = false } = {}) {
    const parsed = this.parseTimeInput(input.value)
    
    if (!parsed || parsed.hours < 0 || parsed.hours > 24) {
      input.setCustomValidity(this.t.validation.time_range)
      input.classList.add('invalid')
      if (report) input.reportValidity()
      return
    }
    
    if (parsed.minutes < 0 || parsed.minutes > 59 || (parsed.hours === 24 && parsed.minutes > 0)) {
      input.setCustomValidity(this.t.validation.invalid_minutes)
      input.classList.add('invalid')
      if (report) input.reportValidity()
      return
    }
    
    input.setCustomValidity('')
    input.classList.remove('invalid')
  }

  async loadExistingData() {
    try {
      this.showLoading(true)
      
      const response = await fetch(`${this.API_BASE_URL}/store`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      
      // Handle location data
      if (data.location) {
        this.renderLocationInfo(data.location)
      }

      // If location type is not synced, hide the editor and show info message
      if (data.synced === false) {
        this.hideEditor()
        return
      }
      
      // Handle opening hours data
      if (data.openingHours && Array.isArray(data.openingHours)) {
        this.currentData = data.openingHours
        this.renderSections(data.openingHours)
      } else {
        // No existing data, create one empty section
        this.addSection()
      }
      
    } catch (error) {
      console.error('Error loading opening hours:', error)
      this.showError(this.t.error.load_failed + ' ' + error.message)
      // Create one empty section as fallback
      this.addSection()
    } finally {
      this.showLoading(false)
    }
  }

  renderSections(sections) {
    // Always clear all existing sections and recreate from data
    const existingSections = this.querySelectorAll('[data-js="section"]')
    existingSections.forEach(section => section.remove())

    if (sections.length === 0) {
      // No data - create default empty section with one empty line
      this.addSection()
    } else {
      // Recreate all sections from data
      sections.forEach((sectionData, index) => {
        this.addSection(sectionData, index)
      })
    }
  }


  addSection(data = null, index = null) {
    const sectionIndex = index !== null ? index : this.getSectionCount()
    const times = data ? data.times || [] : []
    
    // Generate time lines HTML - ensure at least one empty line if no data
    let timeLinesHtml = ''
    if (times.length > 0) {
      timeLinesHtml = times.map((time, timeIndex) => `
        <div class="time-line" data-line-index="${timeIndex}" data-js="time-line">
          <div class="input-wrapper">
          <input type="text" placeholder="${this.t.line_title_placeholder}" class="input line-title-input" data-js="line-title" data-input-type="line-title" value="${time.title || ''}" required/>
          <label for="line-title-${sectionIndex}-${timeIndex}">${this.t.line_title_placeholder}</label>
          </div>
          <div class="input-wrapper">
          <input type="text" inputmode="text" placeholder="${this.t.from_placeholder}" class="input time-input from-input" data-js="time-input from-input" data-input-type="time" value="${time.from !== undefined ? this.formatNumberForInput(time.from) : ''}" ${time.closed ? 'disabled' : 'required'}/>
          <label for="from-input-${sectionIndex}-${timeIndex}">${this.t.from_placeholder}</label>
          </div>
          <div class="input-wrapper">
          <input type="text" inputmode="text" placeholder="${this.t.to_placeholder}" class="input time-input to-input" data-js="time-input to-input" data-input-type="time" value="${time.to !== undefined ? this.formatNumberForInput(time.to) : ''}" ${time.closed ? 'disabled' : 'required'}/>
          <label for="to-input-${sectionIndex}-${timeIndex}">${this.t.to_placeholder}</label>
          </div>
          <div class="checkbox-wrapper">
            <input type="checkbox" class="checkbox closed-checkbox" data-js="closed-checkbox" id="closed_${sectionIndex}-${timeIndex}" ${time.closed ? 'checked' : ''}/>
            <label for="closed_${sectionIndex}-${timeIndex}">
              <span class="rte">${this.t.closed_label}</span>
            </label>
          </div>
          <button class="btn remove-line-btn btn--secondary" data-js="remove-line">${this.t.remove_line}</button>
        </div>
      `).join('')
    } else {
      // Default empty time line
      timeLinesHtml = `
        <div class="time-line" data-line-index="0" data-js="time-line">
          <div class="input-wrapper">
          <input type="text" placeholder="${this.t.line_title_placeholder}" class="input line-title-input" data-js="line-title" data-input-type="line-title" value="" required/>
          <label for="line-title-${sectionIndex}-0">${this.t.line_title_placeholder}</label>
          </div>
          <div class="input-wrapper">
          <input type="text" inputmode="text" placeholder="${this.t.from_placeholder}" class="input time-input from-input" data-js="time-input from-input" data-input-type="time" value="" required/>
          <label for="from-input-${sectionIndex}-0">${this.t.from_placeholder}</label>
          </div>
          <div class="input-wrapper">
          <input type="text" inputmode="text" placeholder="${this.t.to_placeholder}" class="input time-input to-input" data-js="time-input to-input" data-input-type="time" value="" required/>
          <label for="to-input-${sectionIndex}-0">${this.t.to_placeholder}</label>
          </div>
          <div class="checkbox-wrapper">
            <input type="checkbox" class="checkbox closed-checkbox" data-js="closed-checkbox" id="closed_${sectionIndex}-0"/>
            <label for="closed_${sectionIndex}-0">
              <span class="rte">${this.t.closed_label}</span>
            </label>
          </div>
          <button class="btn remove-line-btn btn--secondary" data-js="remove-line">${this.t.remove_line}</button>
        </div>
      `
    }
    
    const sectionHtml = `
      <div class="section-block relative" data-section-index="${sectionIndex}" data-js="section">
        <div class="od-flex-single-child time-line">
          <div class="input-wrapper">
          <input type="text" placeholder="${this.t.section_title_placeholder}" class="input section-title-input" data-js="section-title" data-input-type="section-title" value="${data ? data.sectionTitle || '' : ''}" required>
          <label for="section-title-${sectionIndex}">${this.t.section_title_placeholder}</label>
          </div>
        </div>

        ${timeLinesHtml}

        <div class="section-footer mt-4" data-js="section-footer">
          <div class="checkbox-wrapper">
            <input type="checkbox" name="section_active" class="checkbox section-active-checkbox" data-js="section-active" id="section_active_${sectionIndex}" ${data && data.active ? 'checked' : ''}>
            <label for="section_active_${sectionIndex}">
              <span class="rte">${this.t.section_active_label}</span>
            </label>
          </div>
          <div class="od-flex-single-child flex flex-wrap wrap justify-end">
            <button class="btn btn--secondary add-line-btn" data-js="add-line">${this.t.add_line}</button>
            <button class="remove-section-btn" data-js="remove-section"><svg width="24" height="24" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" fill="none" fill-rule="evenodd" stroke-linejoin="round" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="M5 19 19 5M5 5l14 14"/></svg></button>
          </div>
        </div>
      </div>
    `

    // Insert before the main buttons
    const mainButtons = this.querySelector('[data-opening-hours="main-buttons"]')
    if (mainButtons) {
      mainButtons.insertAdjacentHTML('beforebegin', sectionHtml)
    } else {
      this.insertAdjacentHTML('beforeend', sectionHtml)
    }
  }


  addTimeLine(section, data = null) {
    // Find the section footer to insert before it
    const sectionFooter = section.querySelector('.section-footer')
    const existingTimeLines = section.querySelectorAll('.time-line:not(:first-child)')
    const lineIndex = existingTimeLines.length

    const sectionIndex = section.dataset.sectionIndex || 0
    const uniqueId = `closed_${sectionIndex}_${lineIndex}`
    
    const timeLineHtml = `
      <div class="time-line" data-line-index="${lineIndex}" data-js="time-line">
        <input 
          type="text" 
          placeholder="${this.t.line_title_placeholder}" 
          class="input line-title-input" 
          data-js="line-title"
          data-input-type="line-title"
          value="${data ? data.title || '' : ''}"
          required
        />
        <input 
          type="text" 
          inputmode="text"
          placeholder="${this.t.from_placeholder}" 
          class="input time-input from-input" 
          data-js="time-input from-input"
          data-input-type="time"
          value="${data && data.from !== undefined ? this.formatNumberForInput(data.from) : ''}"
          ${data && data.closed ? 'disabled' : 'required'}
        />
        <input 
          type="text" 
          inputmode="text"
          placeholder="${this.t.to_placeholder}" 
          class="input time-input to-input" 
          data-js="time-input to-input"
          data-input-type="time"
          value="${data && data.to !== undefined ? this.formatNumberForInput(data.to) : ''}"
          ${data && data.closed ? 'disabled' : 'required'}
        />
        <div class="checkbox-wrapper">
          <input type="checkbox" class="checkbox closed-checkbox" data-js="closed-checkbox" id="${uniqueId}" ${data && data.closed ? 'checked' : ''}/>
          <label for="${uniqueId}">
            <span class="rte">${this.t.closed_label}</span>
          </label>
        </div>
        <button type="button" class="btn remove-line-btn btn--secondary" data-js="remove-line">
          ${this.t.remove_line}
        </button>
      </div>
    `

    if (sectionFooter) {
      sectionFooter.insertAdjacentHTML('beforebegin', timeLineHtml)
    } else {
      section.insertAdjacentHTML('beforeend', timeLineHtml)
    }
  }

  removeTimeLine(timeLine) {
    const section = timeLine.closest('[data-js="section"]')
    const existingTimeLines = section.querySelectorAll('[data-js="time-line"]:not(:first-child)')
    
    // Don't allow removing the last time line
    if (existingTimeLines.length <= 1) {
      this.showError(this.t.error.min_one_line)
      return
    }

    timeLine.remove()
    this.updateLineIndices(section)
  }

  removeSection(section) {
    const allSections = this.querySelectorAll('[data-js="section"]')
    
    // Don't allow removing the last section
    if (allSections.length <= 1) {
      this.showError(this.t.error.min_one_section)
      return
    }

    section.remove()
    this.updateSectionIndices()
  }

  updateLineIndices(section) {
    const timeLines = section.querySelectorAll('[data-js="time-line"]:not(:first-child)')
    timeLines.forEach((line, index) => {
      line.dataset.lineIndex = index
    })
  }

  updateSectionIndices() {
    const sections = this.querySelectorAll('[data-js="section"]')
    sections.forEach((section, index) => {
      section.dataset.sectionIndex = index
      
      // Update checkbox ID and label
      const checkbox = section.querySelector('[data-js="section-active"]')
      const label = section.querySelector('label[for^="section_active_"]')
      if (checkbox && label) {
        const newId = `section_active_${index}`
        checkbox.id = newId
        label.setAttribute('for', newId)
      }
    })
  }

  getSectionCount() {
    return this.querySelectorAll('[data-js="section"]').length
  }


  // Single comprehensive validation function that validates inputs AND collects data
  validateAndCollectData() {
    const errorMessages = []
    const sections = []
    
    // Clear all previous invalid states
    this.querySelectorAll('.invalid').forEach(input => {
      input.classList.remove('invalid')
      input.setCustomValidity('')
    })
    
    // Validate and collect data from each section
    const sectionElements = this.querySelectorAll('.section-block')
    
    sectionElements.forEach((sectionEl, sectionIndex) => {

      console.log(sectionIndex)
      const sectionTitle = sectionEl.querySelector('.section-title-input').value.trim()
      const sectionTitleInput = sectionEl.querySelector('.section-title-input')
      const timeLines = sectionEl.querySelectorAll('.time-line:not(:first-child)')
      const active = sectionEl.querySelector('.section-active-checkbox').checked
      
      // Check if section has any content
      const sectionHasContent = this.sectionHasContent(sectionEl)
      const sectionHasLines = timeLines.length > 0
      
      // If section exists (has lines), it needs validation
      if (sectionHasLines) {
        // Always validate section title if section has lines
        if (!sectionTitle) {
          errorMessages.push(`${this.t.error.section_at} ${sectionIndex + 1}: ${this.t.error.section_title_required}`)
          this.markInputInvalid(sectionTitleInput, this.t.error.section_title_required)
        }
        
        // Check if section has at least one line with content
        if (!sectionHasContent) {
          // Section exists but is completely empty - require at least one line with data
          errorMessages.push(`${this.t.error.section_at} "${sectionTitle || `Section ${sectionIndex + 1}`}": ${this.t.error.min_one_line}`)
        }
        
        // Collect and validate lines
        const times = []
        timeLines.forEach((lineEl, lineIndex) => {
          const lineResult = this.validateAndCollectLine(lineEl, lineIndex, sectionTitle || `Section ${sectionIndex + 1}`)
          errorMessages.push(...lineResult.errors)
          if (lineResult.data) {
            times.push(lineResult.data)
          }
        })
        
        // Add section to data if it has content (even if there are validation errors)
        if (sectionHasContent && sectionTitle) {
          sections.push({
            sectionTitle: sectionTitle,
            times: times,
            active: active
          })
        }
      }
    })
    
    // Check if we have at least one section with data
    if (sections.length === 0 && errorMessages.length === 0) {
      errorMessages.push(this.t.error.min_one_section_data)
    }
    
    return {
      valid: errorMessages.length === 0,
      errors: errorMessages,
      data: sections
    }
  }

  // Helper function to check if a section has any content
  sectionHasContent(sectionEl) {
    const timeLines = sectionEl.querySelectorAll('.time-line:not(:first-child)')
    return Array.from(timeLines).some(lineEl => {
      const title = lineEl.querySelector('.line-title-input').value.trim()
      const fromValue = lineEl.querySelector('.from-input').value.trim()
      const toValue = lineEl.querySelector('.to-input').value.trim()
      const closed = lineEl.querySelector('.closed-checkbox').checked
      return title || fromValue || toValue || closed
    })
  }

  // Helper function to validate and collect data from a single line
  validateAndCollectLine(lineEl, lineIndex, sectionTitle) {
    const errors = []
    const title = lineEl.querySelector('.line-title-input').value.trim()
    const titleInput = lineEl.querySelector('.line-title-input')
    const fromInput = lineEl.querySelector('.from-input')
    const toInput = lineEl.querySelector('.to-input')
    const fromValue = fromInput.value.trim()
    const toValue = toInput.value.trim()
    const closed = lineEl.querySelector('.closed-checkbox').checked
    
    // Check if line has any data
    if (title || fromValue || toValue || closed) {
      // Validate line title
      if (!title) {
        errors.push(`${this.t.error.section_at} "${sectionTitle}", ${this.t.error.line_at} ${lineIndex + 1}: ${this.t.error.line_title_required}`)
        this.markInputInvalid(titleInput, this.t.error.line_title_required)
      }
      
      let fromNum = null
      let toNum = null
      
      // Validate time fields if not closed
      if (!closed) {
        // Validate from field
        if (!fromValue) {
          errors.push(`${this.t.error.section_at} "${sectionTitle}", ${this.t.error.line_at} ${lineIndex + 1} (From): ${this.t.error.time_fields_required}`)
          this.markInputInvalid(fromInput, this.t.error.time_fields_required)
        } else {
          this.validateTimeInput(fromInput)
          if (fromInput.classList.contains('invalid')) {
            errors.push(`${this.t.error.section_at} "${sectionTitle}", ${this.t.error.line_at} ${lineIndex + 1} (From): ${fromInput.validationMessage}`)
          } else {
            const parsed = this.parseTimeInput(fromValue)
            fromNum = this.timeToNumber(parsed.hours, parsed.minutes)
          }
        }
        
        // Validate to field
        if (!toValue) {
          errors.push(`${this.t.error.section_at} "${sectionTitle}", ${this.t.error.line_at} ${lineIndex + 1} (To): ${this.t.error.time_fields_required}`)
          this.markInputInvalid(toInput, this.t.error.time_fields_required)
        } else {
          this.validateTimeInput(toInput)
          if (toInput.classList.contains('invalid')) {
            errors.push(`${this.t.error.section_at} "${sectionTitle}", ${this.t.error.line_at} ${lineIndex + 1} (To): ${toInput.validationMessage}`)
          } else {
            const parsed = this.parseTimeInput(toValue)
            toNum = this.timeToNumber(parsed.hours, parsed.minutes)
          }
        }
        
        // Validate time order if both values are valid
        if (fromNum !== null && toNum !== null && fromNum >= toNum) {
          errors.push(`${this.t.error.section_at} "${sectionTitle}", ${this.t.error.line_at} ${lineIndex + 1}: ${this.t.error.from_less_than_to}`)
        }
      }
      
      // Return line data if we have a title (even if there are validation errors)
      if (title) {
        return {
          errors: errors,
          data: {
            title: title,
            from: closed ? null : fromNum,
            to: closed ? null : toNum,
            closed: closed
          }
        }
      }
    }
    
    return { errors: errors, data: null }
  }

  // Helper function to mark an input as invalid
  markInputInvalid(input, message) {
    input.classList.add('invalid')
    input.setCustomValidity(message)
  }

  formatNumberForInput(number) {
    if (number === null || number === undefined) {
      return ''
    }
    const num = Number(number)
    const hours = Math.floor(num)
    const minutes = Math.round((num - hours) * 100)
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
  }

  async saveOpeningHours() {
    try {
      // Single validation step with visual feedback and data collection
      const validationResult = this.validateAndCollectData()
      
      if (!validationResult.valid) {
        // Create a formatted error message with all issues
        const errorList = validationResult.errors.map(msg => `• ${msg}`).join('\n')
        const fullErrorMessage = `Please fix the following issues:\n\n${errorList}`
        
        this.showError(fullErrorMessage)
        
        // Focus the first problematic input
        const firstInvalidInput = this.querySelector('.invalid[data-input-type]')
        if (firstInvalidInput) {
          firstInvalidInput.focus()
        }
        return
      }
      
      const formData = validationResult.data

      this.showLoading(true, this.t.saving)

      const response = await fetch(`${this.API_BASE_URL}/store`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          openingHours: formData
        })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
      }

      const result = await response.json()
      
      this.showSuccess(this.t.success)
      this.currentData = formData


    } catch (error) {
      console.error('Error saving opening hours:', error)
      this.showError(this.t.error.save_failed + ' ' + error.message)
    } finally {
      this.showLoading(false)
    }
  }

  hideEditor() {
    const mainButtons = this.querySelector('[data-opening-hours="main-buttons"]')
    if (mainButtons) {
      mainButtons.style.display = 'none'
    }

    const existingSections = this.querySelectorAll('[data-js="section"]')
    existingSections.forEach(section => section.remove())

    this.showMessage(this.t.not_synced || 'Opening hours management is not available for this location type.', 'info', { persistent: true })
  }

  showLoading(show, message) {
    if (show) {
      this.loadingIndicator.querySelector('p').textContent = message || this.t.loading
      this.backdrop.classList.add('loading-backdrop--visible')
      this.loadingIndicator.classList.add('loading-indicator--visible')
    } else {
      this.backdrop.classList.remove('loading-backdrop--visible')
      this.loadingIndicator.classList.remove('loading-indicator--visible')
    }
  }

  showError(message) {
    this.showMessage(message, 'error')
  }

  showSuccess(message) {
    this.showMessage(message, 'success')
  }

  showMessage(message, type = 'info', options = {}) {
    // Get dedicated message container
    const messageContainer = this.querySelector('[data-opening-hours="messages"]')
    if (!messageContainer) {
      console.error('Message container not found')
      return
    }

    // Remove existing messages
    const existingMessages = messageContainer.querySelectorAll('.opening-hours-message')
    existingMessages.forEach(msg => msg.remove())

    // Create message element with proper CSS classes
    const messageEl = document.createElement('div')
    messageEl.className = `opening-hours-message opening-hours-message--${type}`
    
    // Handle multi-line messages by converting newlines to HTML
    if (message.includes('\n')) {
      messageEl.innerHTML = message.replace(/\n/g, '<br>')
    } else {
      messageEl.textContent = message
    }

    // Insert into dedicated container
    messageContainer.appendChild(messageEl)

    // Auto-remove transient messages (success, etc.) but keep errors and persistent messages
    if (type !== 'error' && !options.persistent) {
      setTimeout(() => {
        if (messageEl.parentNode) {
          messageEl.remove()
        }
      }, 5000)
    }
  }

  renderLocationInfo(location) {
    console.log('renderLocationInfo', location)
    const strings = window.theme?.strings?.opening_hours?.store_details || {}
    
    // Render store name
    const storeNameEl = document.querySelector('[data-store="name"]')
    if (storeNameEl && location.name) {
      storeNameEl.innerHTML = `<strong>${location.name}</strong>`
    }
    
    // Render store address
    const storeAddressEl = document.querySelector('[data-store="address"]')
    if (storeAddressEl && location.address) {
      const address = location.address
      let addressHtml = []
      
      if (address.address1) addressHtml.push(address.address1)
      if (address.address2) addressHtml.push(address.address2)
      if (address.city || address.province || address.zip) {
        let cityLine = []
        if (address.city) cityLine.push(address.city)
        if (address.province) cityLine.push(address.province)
        if (address.zip) cityLine.push(address.zip)
        addressHtml.push(cityLine.join(', '))
      }
      if (address.country) addressHtml.push(address.country)
      
      storeAddressEl.innerHTML = addressHtml.join('<br>')
    }
    
    // Initialize map if coordinates are available
    if (location.coordinates && location.coordinates.lat && location.coordinates.lng) {
      this.initializeMap(location.coordinates.lat, location.coordinates.lng, location.name || strings.map_label || 'Store Location')
    }
  }

  initializeMap(lat, lng, title) {
    const mapElement = document.querySelector('[data-store="map"]')
    if (!mapElement) {
      console.error('Map element not found')
      return
    }
    
    // Check if MapLibre is loaded
    if (typeof maplibregl === 'undefined') {
      this.loadMapLibre().then(() => {
        this.createMap(mapElement, lat, lng, title)
      }).catch(error => {
        console.error('Failed to load MapLibre:', error)
        this.showMapFallback(mapElement, lat, lng)
      })
    } else {
      this.createMap(mapElement, lat, lng, title)
    }
  }

  createMap(mapElement, lat, lng, title) {
    try {
      if (!this.LOCATIONIQ_API_KEY) {
        console.warn('LocationIQ API key not configured, showing fallback')
        this.showMapFallback(mapElement, lat, lng)
        return
      }

      const styleUrl = `${this.LOCATIONIQ_STYLE_URL}?key=${this.LOCATIONIQ_API_KEY}`
      
      const map = new maplibregl.Map({
        container: mapElement,
        style: styleUrl,
        center: [parseFloat(lng), parseFloat(lat)],
        zoom: 15
      })
      
      // Add navigation controls
      map.addControl(new maplibregl.NavigationControl(), 'top-right')
      
      // Add marker
      new maplibregl.Marker()
        .setLngLat([parseFloat(lng), parseFloat(lat)])
        .addTo(map)

    } catch (error) {
      console.error('Error creating map:', error)
      this.showMapFallback(mapElement, lat, lng)
    }
  }

  loadMapLibre() {
    return new Promise((resolve, reject) => {
      // Check if MapLibre is already loaded
      if (typeof maplibregl !== 'undefined') {
        resolve()
        return
      }

      // Load MapLibre CSS if not already present
      if (!document.querySelector('link[href*="maplibre-gl.css"]')) {
        const link = document.createElement('link')
        link.rel = 'stylesheet'
        link.href = 'https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.css'
        document.head.appendChild(link)
      }

      // Create script element to load MapLibre
      const script = document.createElement('script')
      script.src = 'https://unpkg.com/maplibre-gl@4.7.1/dist/maplibre-gl.js'
      script.async = true

      script.onload = () => {
        resolve()
      }

      script.onerror = () => {
        reject(new Error('Failed to load MapLibre GL JS'))
      }

      document.head.appendChild(script)
    })
  }

  showMapFallback(mapElement, lat, lng) {
    // Show a simple fallback with coordinates and link
    mapElement.innerHTML = `
      <div style="display: flex; align-items: center; justify-content: center; height: 100%; background: #f5f5f5; border: 1px solid #ddd; border-radius: 8px;">
        <div style="text-align: center; color: #666;">
          <p><strong>Map Location</strong></p>
          <p>Latitude: ${lat}</p>
          <p>Longitude: ${lng}</p>
          <p><a href="https://maps.google.com/?q=${lat},${lng}" target="_blank" style="color: #007acc;">View on Google Maps</a></p>
        </div>
      </div>
    `
  }

}
if (!customElements.get('opening-hours-manager')) {
  customElements.define('opening-hours-manager', OpeningHoursManager);  
}
